import {
  APIReference,
  APIReference_apiReference as AutoGenAPIReferenceType,
  ClassReferenceParent,
  ReferenceType,
} from '@modules/client';
import AppMainNavbar from '@modules/layout/layout/components/AppMainNavbar';
import AppContent from '@modules/layout/layout/components/AppContent';
import AppHeading from '@modules/layout/layout/components/AppHeading';
import AppLayout, { LayoutType, TOpenGraphMeta } from '@modules/layout/layout/components/AppLayout';
import AppLeftNav from '@modules/layout/layout/components/AppLeftNav';
import AppRightNav from '@modules/layout/layout/components/AppRightNav';
import AppRightNavContent from '@modules/ui/shared/components/AppRightNavContent';
import ShowInViewMetadataProvider from '@modules/ui/shared/components/ShowInViewMetadataProvider';
import SideBarNavigation from '@modules/ui/shared/components/SideBarNavigation';
import useSideBarContent from '@modules/ui/shared/hooks/useSideBarContent';
import { useTranslation } from '@rbx/intl';
import { Grid, Switch, Typography } from '@rbx/ui';
import React, {
  ChangeEvent,
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { TestId } from '@modules/utilities/constants/testConstants';
import AppIAHeading from '@modules/layout/layout/components/AppIAHeading';
import { useAppConfig } from '@modules/layout/layout/components/AppConfigProvider';
import ClassContent from '../../class/components/ClassContent';
import DatatypeContent from '../../datatype/components/DatatypeContent';
import EnumContent from '../../enum/components/EnumContent';
import GlobalOrLibraryContent from '../../globalOrLibrary/components/GlobalOrLibraryContent';
import {
  containsDeprecatedField,
  getAPIReferenceHeadings,
  isClassReference,
  isDatatypeReference,
  isEnumReference,
  isGlobalReference,
  isLibraryReference,
  resolveDeprecatedClass,
  resolveDeprecatedDatatype,
  resolveDeprecatedGlobalOrLibrary,
} from '../../utils/apiReferenceUtils';

interface ReferenceContainerProps {
  title: string;
  apiReference: AutoGenAPIReferenceType | APIReference['apiReference'];
  classReferenceParents: ClassReferenceParent[] | null;
  openGraphMeta: Partial<TOpenGraphMeta>;
  description: string;
}

const ReferenceContainer: FunctionComponent<React.PropsWithChildren<ReferenceContainerProps>> = ({
  title,
  apiReference,
  // FIXME
  classReferenceParents,
  openGraphMeta,
  description,
}) => {
  const { translate } = useTranslation();
  const { getBreadcrumbsItems } = useSideBarContent();
  const { enableNewLayout } = useAppConfig();
  const [showDeprecated, setShowDeprecated] = useState<boolean>(false);
  const toggleDeprecated = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setShowDeprecated(e.target.checked);
  }, []);

  const headings = useMemo(() => {
    if (apiReference === null) {
      return [];
    }
    return getAPIReferenceHeadings(translate, apiReference, classReferenceParents, showDeprecated);
  }, [translate, apiReference, classReferenceParents, showDeprecated]);

  const content = useMemo(() => {
    if (apiReference === null) return null;
    if (isClassReference(apiReference)) {
      return (
        <ClassContent
          classReference={showDeprecated ? apiReference : resolveDeprecatedClass(apiReference)}
          classReferenceParents={
            showDeprecated
              ? classReferenceParents
              : (classReferenceParents ?? []).map(resolveDeprecatedClass)
          }
        />
      );
    }

    if (isLibraryReference(apiReference)) {
      return (
        <GlobalOrLibraryContent
          referenceType={ReferenceType.LIBRARY}
          globalOrLibraryReference={
            showDeprecated ? apiReference : resolveDeprecatedGlobalOrLibrary(apiReference)
          }
        />
      );
    }

    if (isGlobalReference(apiReference)) {
      return (
        <GlobalOrLibraryContent
          referenceType={ReferenceType.GLOBAL}
          globalOrLibraryReference={
            showDeprecated ? apiReference : resolveDeprecatedGlobalOrLibrary(apiReference)
          }
        />
      );
    }

    if (isDatatypeReference(apiReference)) {
      return (
        <DatatypeContent
          datatypeReference={
            showDeprecated ? apiReference : resolveDeprecatedDatatype(apiReference)
          }
        />
      );
    }
    if (isEnumReference(apiReference)) {
      return <EnumContent enumReference={apiReference} />;
    }
    throw Error(`unhandled reference type ${apiReference}`);
  }, [apiReference, classReferenceParents, showDeprecated]);

  const breadcrumbsItems = useMemo(() => {
    return getBreadcrumbsItems(translate);
  }, [getBreadcrumbsItems, translate]);

  const locationHash = typeof window !== 'undefined' ? window.location.hash.substring(1) : null;

  useEffect(() => {
    if (locationHash && apiReference) {
      if (isClassReference(apiReference)) {
        if (
          containsDeprecatedField(apiReference, locationHash) ||
          (classReferenceParents &&
            classReferenceParents.find((parentReference) =>
              containsDeprecatedField(parentReference, locationHash),
            ))
        ) {
          setShowDeprecated(true);
        }
      } else if (containsDeprecatedField(apiReference, locationHash)) {
        setShowDeprecated(true);
      }
    }
  }, [apiReference, classReferenceParents, locationHash]);

  if (apiReference === null) {
    return (
      <AppLayout
        testid={TestId.ReferenceContainer}
        openGraphMeta={openGraphMeta}
        pageTitle={translate('Label.NullReference') || 'This reference is null!'}
        pageMetaDescription={translate('Label.NullReference') || 'This reference is null!'}
        headings={headings}>
        <AppContent>
          <Typography>{translate('Label.NullReference') || 'This reference is null!'}</Typography>
        </AppContent>
        <AppLeftNav>
          <SideBarNavigation />
        </AppLeftNav>
      </AppLayout>
    );
  }

  const { name } = apiReference;
  let shouldShowToggleSwitch = false;

  if (!isEnumReference(apiReference)) {
    shouldShowToggleSwitch = true;
  }

  return (
    <AppLayout
      testid={TestId.ReferenceContainer}
      pageTitle={title}
      openGraphMeta={openGraphMeta}
      headings={headings}
      pageMetaDescription={description}
      type={LayoutType.ApiReference}>
      <AppMainNavbar pageTitle={name} breadcrumbsItems={breadcrumbsItems} />
      <AppIAHeading breadcrumbsItems={breadcrumbsItems} />
      <AppHeading
        title={name}
        adornment={
          shouldShowToggleSwitch && (
            <Grid
              container
              alignItems='center'
              justifyContent={enableNewLayout ? 'flex-start' : 'flex-end'}
              spacing={1}>
              <Grid item>
                <Switch
                  size='small'
                  color='primary'
                  checked={showDeprecated}
                  onChange={toggleDeprecated}
                  aria-label={translate('Label.ShowDeprecated') || 'Show Deprecated'}
                />
              </Grid>
              <Grid item>
                <Typography variant='body2'>
                  {translate('Label.ShowDeprecated') || 'Show Deprecated'}
                </Typography>
              </Grid>
            </Grid>
          )
        }
      />
      <AppRightNav content={<AppRightNavContent />} />
      <AppContent>
        <ShowInViewMetadataProvider>{content}</ShowInViewMetadataProvider>
      </AppContent>
      <AppLeftNav>
        <SideBarNavigation />
      </AppLeftNav>
    </AppLayout>
  );
};

export default ReferenceContainer;
